import { useEffect, useRef, useCallback } from "react";

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  threshold?: number;
}

export function useInfiniteScroll({
  hasMore,
  loading,
  onLoadMore,
  threshold = 100,
}: UseInfiniteScrollOptions) {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;

    // Find the ScrollArea viewport element
    const container = containerRef.current;
    if (!container) return;

    const scrollAreaViewport = container.closest(
      '[data-slot="scroll-area-viewport"]',
    ) as HTMLElement;
    if (!scrollAreaViewport) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollAreaViewport;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < threshold) {
      onLoadMore();
    }
  }, [loading, hasMore, onLoadMore, threshold]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Find the ScrollArea viewport element
    const scrollAreaViewport = container.closest(
      '[data-slot="scroll-area-viewport"]',
    ) as HTMLElement;
    if (!scrollAreaViewport) return;

    scrollAreaViewport.addEventListener("scroll", handleScroll);
    return () => scrollAreaViewport.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return containerRef;
}
